import type { SuperHubStore } from '..';
import type { GitSidebarCategory, GitSidebarQueryString } from '@readme/api-contracts/schemas/sidebar';
import type { ReactNode } from 'react';

import { dequal } from 'dequal/lite';
import { useEffect, useRef } from 'react';

import useReadmeApi from '@core/hooks/useReadmeApi';

import { useSuperHubStore } from '..';

/**
 * Map of route sections to the corresponding `/sidebar?page_type` endpoint.
 */
export const superHubSidebarTypes = {
  api_definitions: null,
  changelog: 'changelog',
  custom_components: null,
  discuss: null,
  docs: 'guide',
  home: null,
  page: 'custom_page',
  recipes: null,
  reference: 'reference',
  reusable_content: null,
} satisfies Record<NonNullable<SuperHubStore['routeSection']>, GitSidebarQueryString['page_type'] | null>;

/**
 * Custom SWR compare function that considers `__headers__.etag` changes.
 * Our fetcher attaches `__headers__` to the returned sidebar array. SWR's
 * default compare may treat arrays as equal even when only this non-index
 * metadata changes, which can leave `etag` stale after revalidation and
 * cause 412s on subsequent reorder requests.
 */
export function sidebarCompare(a: unknown, b: unknown) {
  const aHeaders = (a as { __headers__?: Record<string, string | undefined> })?.__headers__;
  const bHeaders = (b as { __headers__?: Record<string, string | undefined> })?.__headers__;
  if (aHeaders?.etag !== bHeaders?.etag) return false;
  return dequal(a, b);
}

interface ConnectSuperHubSidebarToApiProps {
  children: ReactNode;
  /**
   * Very first sidebar data that is populated during SSR and the first CSR.
   */
  initialSidebar: GitSidebarCategory[] | null;
}

/**
 * Connects our `SuperHubStore` sidebar slice to the git sidebar API endpoint to
 * continually hydrate its data and keeps it in sync.
 */
export function ConnectSuperHubSidebarToApi({ children, initialSidebar }: ConnectSuperHubSidebarToApiProps) {
  const [getApiBaseUrl, initialize, routeSection, isI18nEnabled, language] = useSuperHubStore(s => [
    s.getApiBaseUrl,
    s.sidebar.initialize,
    s.routeSection,
    s.isI18nEnabled,
    s.language,
  ]);
  const initialSidebarRef = useRef(initialSidebar);

  // Validate sidebar data only when a valid page type exists.
  const pageType = routeSection ? superHubSidebarTypes[routeSection] : null;
  // Include a language param for changelogs if i18n is enabled. Translations for other sidebar
  // types are stored in git branches and don't need a lang param.
  const langParam = isI18nEnabled && pageType === 'changelog' ? `&lang=${language}` : '';
  const apiUrl = pageType ? `/sidebar?page_type=${pageType}${langParam}` : null;

  const {
    data,
    error = null,
    etag,
    isLoading,
    swrKey,
  } = useReadmeApi<GitSidebarCategory[]>(apiUrl, {
    apiBaseUrl: getApiBaseUrl(),
    swr: {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
      compare: sidebarCompare,
    },
  });

  useEffect(() => {
    initialize({
      data: data || initialSidebarRef.current || [],
      error,
      etag,
      isLoading,
      swrKey,
    });

    // After first connection is established to our SWR endpoints, discard the
    // initial value refs b/c we no longer need it as a fallback.
    if (!isLoading) {
      initialSidebarRef.current = null;
    }
  }, [data, error, etag, initialize, isLoading, swrKey]);

  return <>{children}</>;
}
